/* eslint-disable react/display-name */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { fabric } from 'fabric'
import History from './history'

const SketchField = forwardRef((props, ref) => {
  const [action, setAction] = useState(true)

  const _fc = useRef(null)
  const _container = useRef(null)
  const _canvas = useRef(null)
  const _history = useRef(null)

  const _initPencil = () => {
    _fc.current.freeDrawingBrush.width = props.lineWidth
    _fc.current.freeDrawingBrush.color = props.lineColor
  }

  /**
   * Disable touch Scrolling on Canvas
   */
  const disableTouchScroll = () => {
    let canvas = _fc.current
    if (canvas.allowTouchScrolling) {
      canvas.allowTouchScrolling = false
    }
  }

  /**
   * Convert stroke into white color and background into black
   */
  const prepareMask = (filledColor, callback) => {
    const json = _fc.current.toJSON()
    if (json.objects.length === 0) return callback(json.objects)
    json.background = '#000'
    if (filledColor)
      json.objects = json.objects.map((obj) => {
        obj.stroke = filledColor
        return obj
      })
    _fc.current.loadFromJSON(json)
    callback(json.objects)
  }

  /**
   * Action when an object is added to the canvas
   */
  const _onObjectAdded = (e) => {
    if (!action) {
      setAction(true)
      return
    }
    let obj = e.target
    obj.__version = 1
    // Record current object state as JSON and save as originalState
    let objState = obj.toJSON()
    obj.__originalState = objState
    let state = JSON.stringify(objState)
    // Object, previous state, current state
    _history.current.keep([obj, state, state])
  }

  /**
   * Action when an object is removed from the canvas
   */
  const _onObjectRemoved = (e) => {
    let obj = e.target
    if (obj.__removed) {
      obj.__version += 1
      return
    }
    obj.__version = 0
  }

  /**
   * Track the resize of the window and update our state
   *
   * @param e the resize event
   * @private
   */
  const _resize = (e, canvasWidth = null, canvasHeight = null) => {
    if (e) e.preventDefault()
    let { widthCorrection, heightCorrection } = props
    let canvas = _fc.current
    let { offsetWidth, clientHeight } = _container.current
    let prevWidth = canvasWidth || canvas.getWidth()
    let prevHeight = canvasHeight || canvas.getHeight()
    let wfactor = ((offsetWidth - widthCorrection) / prevWidth).toFixed(2)
    let hfactor = ((clientHeight - heightCorrection) / prevHeight).toFixed(2)
    canvas.setWidth(offsetWidth - widthCorrection)
    canvas.setHeight(clientHeight - heightCorrection)
    if (canvas.backgroundImage) {
      // Need to scale background images as well
      let bi = canvas.backgroundImage
      bi.width = bi.width * wfactor
      bi.height = bi.height * hfactor
    }
    let objects = canvas.getObjects()
    for (let i in objects) {
      let obj = objects[i]
      let scaleX = obj.scaleX
      let scaleY = obj.scaleY
      let left = obj.left
      let top = obj.top
      let tempScaleX = scaleX * wfactor
      let tempScaleY = scaleY * hfactor
      let tempLeft = left * wfactor
      let tempTop = top * hfactor
      obj.scaleX = tempScaleX
      obj.scaleY = tempScaleY
      obj.left = tempLeft
      obj.top = tempTop
      obj.setCoords()
    }
    canvas.renderAll()
    canvas.calcOffset()
  }

  /**
   * Sets the background color for this sketch
   * @param color in rgba or hex format
   */
  const _backgroundColor = (color) => {
    if (!color) return
    let canvas = _fc.current
    canvas.setBackgroundColor(color, () => canvas.renderAll())
  }

  /**
   * Perform an undo operation on canvas
   */
  const undo = () => {
    let history = _history.current
    let [obj, prevState, currState] = history.getCurrent()
    history.undo()
    if (obj.__removed) {
      setAction(false)
      _fc.current.add(obj)
      obj.__version -= 1
      obj.__removed = false
    } else if (obj.__version <= 1) {
      _fc.current.remove(obj)
    } else {
      obj.__version -= 1
      obj.setOptions(JSON.parse(prevState))
      obj.setCoords()
      _fc.current.renderAll()
    }
    if (props.onChange) {
      props.onChange()
    }
  }

  /**
   * Perform a redo operation on canvas
   */
  const redo = () => {
    let history = _history.current
    if (history.canRedo()) {
      let canvas = _fc.current
      let [obj, prevState, currState] = history.redo()
      if (obj.__version === 0) {
        setAction(false)
        canvas.add(obj)
        obj.__version = 1
      } else {
        obj.__version += 1
        obj.setOptions(JSON.parse(currState))
      }
      obj.setCoords()
      canvas.renderAll()
      if (props.onChange) {
        props.onChange()
      }
    }
  }

  /**
   * Exports canvas element to a dataurl image
   *
   * @returns {String} Data URL
   */
  const toDataURL = (options) => _fc.current.toDataURL(options)

  /**
   * Returns JSON representation of canvas
   *
   * @param propertiesToInclude Array <optional> Any properties to include
   * @returns {string} JSON string
   */
  const toJSON = (propertiesToInclude) => _fc.current.toJSON(propertiesToInclude)

  /**
   * Populates canvas with data from the specified JSON
   *
   * @param json JSON string or object
   */
  const fromJSON = (json) => {
    if (!json) return
    let canvas = _fc.current
    setTimeout(() => {
      canvas.loadFromJSON(json, () => {
        canvas.renderAll()
        if (props.onChange) {
          props.onChange()
        }
      })
    }, 100)
  }

  /**
   * Clear the content of the canvas
   *
   * @param propertiesToInclude Array <optional> Any properties to include
   * @returns {string} JSON string of the canvas just cleared
   */
  const clear = (propertiesToInclude) => {
    let discarded = toJSON(propertiesToInclude)
    _fc.current.clear()
    _history.current.clear()
    return discarded
  }

  // Expose methods and properties to parent via ref
  useImperativeHandle(ref, () => ({
    prepareMask,
    undo,
    redo,
    clear,
    toDataURL,
    fromJSON,
    toJSON,
    getCanvas: () => _fc.current, // Expose the fabric canvas if needed
  }))

  useEffect(() => {
    let { value, defaultValue, backgroundColor } = props

    let canvas = new fabric.Canvas(_canvas.current)
    canvas.isDrawingMode = true
    _fc.current = canvas

    _initPencil()

    // Set initial background color
    _backgroundColor(backgroundColor)

    // Control resize
    window.addEventListener('resize', _resize, false)

    // Initialize History, with maximum number of undo steps
    _history.current = new History(25)

    // Events binding
    canvas.on('object:added', _onObjectAdded)
    canvas.on('object:removed', _onObjectRemoved)

    disableTouchScroll()

    _resize()

    // Initialize canvas with controlled value if exists
    ;(value || defaultValue) && fromJSON(value || defaultValue)

    return () => {
      // ComponentWillUnmount
      window.removeEventListener('resize', _resize)
    }
  }, [])

  useEffect(() => {
    _resize()
  }, [props.width, props.height, props.style])

  useEffect(() => {
    _backgroundColor(props.backgroundColor)
  }, [props.backgroundColor])

  useEffect(() => {
    if (props.value || props.forceValue) {
      fromJSON(props.value)
    }
  }, [props.value, props.forceValue])

  useEffect(() => {
    _initPencil()
  }, [props.lineColor, props.lineWidth])

  let { className, style, width, height } = props

  let canvasDivStyle = Object.assign(
    {},
    style ? style : {},
    width ? { width: width } : {},
    height ? { height: height } : { height: 512 }
  )

  return (
    <div className={className} ref={_container} style={canvasDivStyle}>
      <canvas id={'id-1'} ref={_canvas}></canvas>
    </div>
  )
})

SketchField.defaultProps = {
  lineColor: 'black',
  lineWidth: 10,
  backgroundColor: 'transparent',
  widthCorrection: 0,
  heightCorrection: 0,
  forceValue: false,
}

export default SketchField
